import React, { useEffect, useCallback, useRef, useMemo } from 'react';
import { useTranslations } from 'js/hooks/i18n';
import { useDeviceSelector } from 'js/store/hooks';
import { compose, prop } from 'ramda';

import withField from '../withField';
import { Control } from '../Control';
import DropDownIcon from '../Icons/DropDownIcon';
import ValidationIcon from '../Icons/ValidationIcon';

import style from './Select.scss';

export const SelectBare = ({
    /* rff render props */
    input,
    meta = {},

    /* user provided props */
    id,
    name,
    label,
    hint,
    bottomHint,
    placeholder = '',
    className,
    classNames,
    options,
    disabled,
    readOnly,
    setValue,
    parse,
    onMouseDown,
    infoTooltip,
    isLabelAsteriskEnabled,
    isPlaceholderAsteriskEnabled,

    ...selectDomProps
}) => {
    const selectRef = useRef();
    const translate = useTranslations();
    const isDesktop = useDeviceSelector(prop('isDesktop'));
    const preventClick = useCallback(event => event.preventDefault(), []);
    const selectClassName = useMemo(() => [
        style.select,
        placeholder && (input.value == null || input.value === '') ? style.empty : ''
    ].filter(Boolean).join(' '), [placeholder, input.value]);

    useEffect(() => {
        if (placeholder && !input.value || !options.length || !setValue) {
            return;
        }

        const isValueMissing = options.every(item => item.value.toString() !== input.value.toString());

        if (isValueMissing) {
            const newValue = parse ? parse(options[0].value) : options[0].value;

            setValue(newValue);
        }
    }, [input.value, options, parse, setValue, placeholder]);

    useEffect(() => {
        if (isDesktop && !disabled && meta.active) {
            selectRef?.current?.focus();
        }
    }, [isDesktop, disabled, meta.active]);

    return (
        <Control
            {...{ id, meta, className, label, infoTooltip, isLabelAsteriskEnabled, hint, bottomHint, readOnly, animate: true }}
            classNames={{
                content: `${style.content} ${disabled || readOnly ? style.disabled : ''}`,
                ...classNames
            }}
        >
            <select
                id={id}
                ref={selectRef}
                tabIndex={disabled || readOnly ? -1 : 0}
                className={selectClassName}
                name={name}
                {...input}
                {...selectDomProps}
                aria-label={selectDomProps['aria-label'] || translate('playtech.mobile.system.select.aria-label')}
                {...meta.submitting ? { disabled: true } : {}}
                disabled={disabled}
                onMouseDown={readOnly ? preventClick : onMouseDown}
            >
                {(placeholder || isPlaceholderAsteriskEnabled) && (
                    <option disabled value="" className="hidden">
                        {`${placeholder}${isPlaceholderAsteriskEnabled ? '*' : ''}`}
                    </option>
                )}
                {options.map(item => (
                    <option
                        key={item.value}
                        value={item.value}
                        disabled={item.disabled}
                        className={classNames.option || ''}
                    >
                        {item.label}
                    </option>
                ))}
            </select>

            {meta.touched && !meta.active && !readOnly && (
                <ValidationIcon
                    animate={true}
                    isValid={!meta.error}
                    aria-label={meta.error}
                    tabIndex={meta.error ? 0 : -1}
                    className={classNames.validationIcon}
                />
            )}

            <DropDownIcon disabled={disabled || readOnly} />
        </Control>
    );
};

SelectBare.defaultProps = {
    options: [],
    className: '',
    classNames: {},
    placeholder: null
};

export const withFieldProps = Wrapped => props =>
    <Wrapped
        enableSelfValueChange={true}
        {...props}
    />;

const enhance = compose(
    withFieldProps,
    withField
);

export const Select = enhance(SelectBare);
